/** Inner content selectors for print sections that fill one viewport without scaling. */
const FULL_VIEWPORT_SECTION_INNER_SELECTORS: Readonly<Record<string, string>> = {
  hero: '#hero',
  'looking-ahead': '#looking-ahead',
  'whats-possible-at-maturity': '#whats-possible-at-maturity',
};

/**
 * @param sectionId - `data-report-section-id` value.
 * @returns Inner element selector when the section uses viewport lock, else null.
 */
export const resolveFullViewportInnerSelector = (
  sectionId: string | null,
): string | null => {
  if (!sectionId) {
    return null;
  }
  return FULL_VIEWPORT_SECTION_INNER_SELECTORS[sectionId] ?? null;
};

/**
 * @param element - Element to stretch within its parent.
 */
const lockElementToParent = (element: HTMLElement): void => {
  element.style.minHeight = '100%';
  element.style.height = '100%';
  element.style.maxHeight = '100%';
  element.style.overflow = 'hidden';
};

/**
 * Pins a print section and its inner hosted content to one viewport (no zoom scaling).
 * @param section - Outer `.report-print-section` element.
 * @param fit - Inner `.report-print-section__fit` wrapper.
 * @param innerSelector - Query selector for the hosted section root inside `fit`.
 */
export const applyFullViewportPrintSectionLock = (
  section: HTMLElement,
  fit: HTMLElement,
  innerSelector: string,
): void => {
  section.style.minHeight = '100vh';
  section.style.height = '100vh';
  section.style.maxHeight = '100vh';
  section.style.overflow = 'hidden';
  section.style.boxSizing = 'border-box';
  lockElementToParent(fit);
  const inner = fit.querySelector<HTMLElement>(innerSelector);
  if (inner) {
    lockElementToParent(inner);
  }
};

/**
 * Clears inline fit styles on full-viewport inner section roots.
 * @param fit - Inner print wrapper.
 * @param resetStyles - Clears zoom/transform/height overrides on one element.
 */
export const resetFullViewportPrintInnerElements = (
  fit: HTMLElement,
  resetStyles: (element: HTMLElement) => void,
): void => {
  Object.values(FULL_VIEWPORT_SECTION_INNER_SELECTORS).forEach(selector => {
    const element = fit.querySelector<HTMLElement>(selector);
    if (element) {
      resetStyles(element);
    }
  });
};
